import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './LandingPage.css';
import Navbar from '../Navigation/Navbar';
import Login from '../Login/Login';

import { isAuthenticated, isStaff, getCurrentUser } from '../../api/users_institutions';
import { R2_MEDIA_BASE_URL } from '../../config/mediaConfig';

function LandingPage() {
    const { t } = useTranslation();
    const [LoggingIn, setLoggingIn] = useState(false);
    const userIsAuthenticated = isAuthenticated();
    const hasToken = !!localStorage.getItem('authToken');
    const userIsStaff = isStaff();
    const currentUser = getCurrentUser();

    const navigate = useNavigate();

    const customSmoothScrollToBottom = () => {
        const startY = window.scrollY;
        const targetY = document.body.scrollHeight - window.innerHeight;
        const distance = targetY - startY;
        const duration = 1200; 
        let start = null;

        window.requestAnimationFrame(function step(timestamp) {
            if (!start) start = timestamp;
            const progress = timestamp - start;
            const percent = Math.min(progress / duration, 1);
            
            const easing = percent < 0.5 
                ? 4 * percent * percent * percent 
                : 1 - Math.pow(-2 * percent + 2, 3) / 2;
            
            window.scrollTo(0, startY + (distance * easing));
            
            if (progress < duration) {
                window.requestAnimationFrame(step);
            }
        });
    }

    useEffect(() => {
        // Hide global loader after component mounts
        const loader = document.getElementById('globalLoader');
        if (loader) loader.style.display = 'none';

        const observerOptions = {
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    
                    const fillCircle = entry.target.querySelector('.stat-ring-fill');
                    if (fillCircle) {
                        fillCircle.style.strokeDashoffset = fillCircle.getAttribute('data-target');
                    }
                }
            });
        }, observerOptions);

        document.querySelectorAll('.fade-in-section, .stat-item').forEach(el => observer.observe(el));

        if (userIsAuthenticated) {
            setLoggingIn(false);
            window.location.href = '/dashboard';
        }

        return () => observer.disconnect();
    }, [userIsAuthenticated]);

    return (
        <>
            <Navbar />
            <div id="globalLoader" className="globalLoader">
                <div className="spinner"></div>
            </div>
            <div id="landingPage" className="page-transition">
                <section className="hero-section">
                <div className="hero-bg-canvas">
                    <div className="hero-blob blob-1"></div>
                    <div className="hero-blob blob-2"></div>
                    <div className="hero-blob blob-3"></div>

                    <div className="hero-deco" style={{width:'120px',height:'120px',border:'3px solid rgba(99,102,241,0.15)',top:'12%',left:'6%',animationDuration:'9s',animationDelay:'0s'}}></div>
                    <div className="hero-deco" style={{width:'60px',height:'60px',background:'rgba(199,210,254,0.35)',top:'20%',left:'18%',animationDuration:'7s',animationDelay:'1s'}}></div>
                    <div className="hero-deco" style={{width:'90px',height:'90px',border:'2px solid rgba(165,180,252,0.2)',top:'8%',left:'42%',animationDuration:'11s',animationDelay:'2s'}}></div>
                    <div className="hero-deco" style={{width:'160px',height:'160px',border:'2.5px solid rgba(99,102,241,0.12)',top:'5%',right:'8%',animationDuration:'13s',animationDelay:'0.5s'}}></div>
                    <div className="hero-deco" style={{width:'45px',height:'45px',background:'rgba(165,180,252,0.3)',top:'22%',right:'18%',animationDuration:'8s',animationDelay:'3s'}}></div>
                    <div className="hero-deco" style={{width:'70px',height:'70px',border:'2px solid rgba(129,140,248,0.2)',top:'48%',left:'3%',animationDuration:'10s',animationDelay:'1.5s'}}></div>
                    <div className="hero-deco" style={{width:'30px',height:'30px',background:'rgba(224,231,255,0.6)',top:'50%',left:'50%',animationDuration:'6s',animationDelay:'2.5s'}}></div>
                    <div className="hero-deco" style={{width:'100px',height:'100px',border:'2px solid rgba(129,140,248,0.15)',top:'42%',right:'4%',animationDuration:'12s',animationDelay:'1s'}}></div>
                    <div className="hero-deco" style={{width:'50px',height:'50px',background:'rgba(199,210,254,0.28)',bottom:'18%',left:'10%',animationDuration:'9s',animationDelay:'4s'}}></div>
                    <div className="hero-deco" style={{width:'80px',height:'80px',border:'2px solid rgba(165,180,252,0.18)',bottom:'12%',left:'46%',animationDuration:'10s',animationDelay:'0s'}}></div>
                    <div className="hero-deco" style={{width:'130px',height:'130px',border:'2px solid rgba(99,102,241,0.1)',bottom:'8%',right:'6%',animationDuration:'14s',animationDelay:'2s'}}></div>
                    <div className="hero-deco" style={{width:'38px',height:'38px',background:'rgba(129,140,248,0.25)',bottom:'25%',right:'20%',animationDuration:'7s',animationDelay:'3.5s'}}></div>
                </div>

                <div className="hero-left">
                    <h1 className="hero-title-landingPage">
                    {t('landingPage.heroTitlePrefix')} <em>{t('landingPage.heroTitleEmphasis')}</em><br />{t('landingPage.heroTitleSuffix')}
                    </h1>
                    <p className="hero-subtitle-landingPage">
                    {t('landingPage.heroSubtitle')}
                    </p>
                    <div className="hero-actions-landingPage">
                    <button className="btn-hero-primary-landingPage" onClick={() => (window.location.href = '/login')}>
                        {t('common.logIn')}
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                    <button className="btn-hero-secondary-landingPage" onClick={() => customSmoothScrollToBottom()}>
                        {t('landingPage.learnMore')}
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7"/></svg>
                    </button>
                    </div>
                </div>

                <div className="hero-right">
                    <div className="hero-image-frame">
                        <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt={t('landingPage.heroImageAlt')} />
                    </div>
                </div>
            </section>

                <section id="about" className="fade-in-section" style={{padding: '80px 8%', background: 'white'}}>
                    <div style={{maxWidth: '900px', margin: '0 auto', textAlign: 'center'}}>
                        <h2 style={{fontSize: '24pt'}}>{t('landingPage.missionTitle')}</h2>
                        <p style={{color: 'var(--text-muted)', fontSize: '14pt', lineHeight: '1.6'}}>
                            {t('landingPage.missionText')}
                        </p>
                    </div>
                </section>

                <section id="did-you-know" style={{background: '#eef2ff', paddingTop: '80px'}}>

                    <div className="fade-in-section" style={{maxWidth: '1100px', margin: '0 auto', padding: '0 8%', textAlign: 'center'}}>
                        <h2 style={{position: 'relative', display: 'inline-block', background: '#ffffff', padding: '25px 60px', borderRadius: '20px', fontSize: '24pt', color: 'var(--text-main)', margin: '0', boxShadow: '0 10px 25px rgba(79, 70, 229, 0.08)', border: '2px solid #ffffff'}}>
                            <span style={{color: 'var(--accent)', fontWeight: '800'}}>{t('landingPage.didYouKnowBadge')}</span> {t('landingPage.didYouKnowTitle')}
                            <img src={`${R2_MEDIA_BASE_URL}/landing_page/lebaneseflaggif.gif`} alt={t('landingPage.lebaneseFlagAlt')} style={{position: 'absolute', right: '-25px', top: '-30px', width: '90px', height: 'auto', transform: 'rotate(10deg)', zIndex: '10'}} />
                        </h2>
                    </div>

                    <div style={{maxWidth: '1100px', margin: '0 auto', padding: '0 8%'}}>

                        <div className="stats-stack fade-in-section">
                        <div className="stat-item">
                            <img src={`${R2_MEDIA_BASE_URL}/landing_page/sadkid2.png`} alt={t('landingPage.stats.helpSeeking.imageAlt')} className="deco-sadkid" />
                            <div className="stat-ring-wrap">
                            <svg width="210" height="210" viewBox="0 0 210 210">
                                <circle className="stat-ring-bg" cx="105" cy="105" r="88"/>
                                <circle className="stat-ring-fill" cx="105" cy="105" r="88"
                                strokeDasharray="552.9"
                                strokeDashoffset="552.9"
                                data-target="525.3"/>
                            </svg>
                            <div className="stat-ring-inner">
                                <span className="stat-ring-number">{t('landingPage.stats.helpSeeking.value')}</span>
                            </div>
                            </div>
                            <span className="stat-text-sub">{t('landingPage.stats.helpSeeking.label')}</span>
                            <span className="stat-text-label">{t('landingPage.stats.helpSeeking.desc')}</span>
                        </div>

                        <div className="stat-divider"></div>

                        <div className="stat-item">
                            <img src={`${R2_MEDIA_BASE_URL}/landing_page/inflation.png`} alt={t('landingPage.stats.trauma.imageAlt')} className="deco-lebanonwar" />
                            <div className="stat-ring-wrap">
                            <svg width="210" height="210" viewBox="0 0 210 210">
                                <circle className="stat-ring-bg" cx="105" cy="105" r="88"/>
                                <circle className="stat-ring-fill" cx="105" cy="105" r="88"
                                strokeDasharray="552.9"
                                strokeDashoffset="552.9"
                                data-target="165.9"/>
                            </svg>
                            <div className="stat-ring-inner">
                                <span className="stat-ring-number">{t('landingPage.stats.trauma.value')}</span>
                            </div>
                            </div>
                            <span className="stat-text-sub">{t('landingPage.stats.trauma.label')}</span>
                            <span className="stat-text-label">{t('landingPage.stats.trauma.desc')}</span>
                        </div>

                        <div className="stat-divider"></div>

                        <div className="stat-item">
                            <img src={`${R2_MEDIA_BASE_URL}/landing_page/politic.png`} alt={t('landingPage.stats.students.imageAlt')} className="deco-politic" />
                            <div className="stat-ring-wrap">
                            <svg width="210" height="210" viewBox="0 0 210 210">
                                <circle className="stat-ring-bg" cx="105" cy="105" r="88"/>
                                <circle className="stat-ring-fill" cx="105" cy="105" r="88"
                                strokeDasharray="552.9"
                                strokeDashoffset="552.9"
                                data-target="368.8"/>
                            </svg>
                            <div className="stat-ring-inner">
                                <span className="stat-ring-number" style={{fontSize:'2.2rem'}}>{t('landingPage.stats.students.value')}</span>
                            </div>
                            </div>
                            <span className="stat-text-sub">{t('landingPage.stats.students.label')}</span>
                            <span className="stat-text-label">{t('landingPage.stats.students.desc')}</span>
                        </div>
                        </div>

                    </div>

                    <div className="rows-container fade-in-section">
                        <div className="content-row">
                            <div className="row-text">
                                <h3>{t('landingPage.silentCrisis.title')}</h3>
                                <p>{t('landingPage.silentCrisis.p1')}</p>
                                <p>{t('landingPage.silentCrisis.p2')}</p>
                            </div>
                            <div className="row-image-placeholder">
                                <img src={`${R2_MEDIA_BASE_URL}/landing_page/sadkid.png`} alt={t('landingPage.silentCrisis.imageAlt')} />
                            </div>
                        </div>

                        <div className="content-row">
                            <div className="row-image-placeholder">
                                <img src={`${R2_MEDIA_BASE_URL}/landing_page/traumatized.png`} alt={t('landingPage.tollOfConflict.imageAlt')} />
                            </div>
                            <div className="row-text">
                                <h3>{t('landingPage.tollOfConflict.title')}</h3>
                                <p>{t('landingPage.tollOfConflict.p1')}</p>
                                <p>{t('landingPage.tollOfConflict.p2')}</p>
                            </div>
                        </div>

                        <div className="content-row">
                            <div className="row-text">
                                <h3>{t('landingPage.everyWordMatters.title')}</h3>
                                <p>{t('landingPage.everyWordMatters.p1')}</p>
                                <p>{t('landingPage.everyWordMatters.p2')}</p>
                            </div>
                            <div className="row-image-placeholder">
                                <img src={`${R2_MEDIA_BASE_URL}/landing_page/reassurance.png`} alt={t('landingPage.everyWordMatters.imageAlt')} />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cta-section fade-in-section">
                <div className="cta-bg-circle"></div>
                <h2 className="cta-title">{t('landingPage.ctaTitle')}</h2>
                <p className="cta-desc">
                    {t('landingPage.ctaDesc')}
                </p>
                <button className="btn-cta" onClick={() => document.getElementById('about')?.scrollIntoView({behavior: 'smooth'})}>
                    {t('landingPage.aboutUsButton')}
                </button>
                </section>
                <section className="fade-in-section">
                    <footer className="footer-main" style={{marginTop: '0'}}>
                        <div className="footer-left">
                            <div className="footer-logo">MindfulCare</div>
                            <p>{t('footer.tagline')}</p>
                        </div>

                        <div className="footer-center">
                            <a onClick={() => navigate('/privacy-policy')} className="footer-link">{t('footer.legalPolicies')}</a>
                            <p>{t('footer.contactPrefix')} <a href="mailto:MindfulCare@gmail.com" style={{color: 'rgba(255,255,255,0.7)', textDecoration: 'none'}}>MindfulCare@gmail.com</a></p>
                        </div>

                        <div className="footer-right">
                            <p>{t('footer.copyright')}</p>
                        </div>
                    </footer>
                </section>
            </div>
        </>
    );
}

export default LandingPage;
